<script lang="ts">
  import ActionsMenu, { type ActionItem } from './ActionsMenu.svelte';
  import Icon from './Icon.svelte';
  import POICard from './POICard.svelte';
  import MapView from './MapView.svelte';
  import SearchInput from './SearchInput.svelte';
  import MapSidebar from './MapSidebar.svelte';
  import GroupCard from './GroupCard.svelte';
  import AutoGroupCard from './AutoGroupCard.svelte';
  import GroupModal from './GroupModal.svelte';
  import EditGroupsSheet from './EditGroupsSheet.svelte';
  import ManageGroupsModal from './ManageGroupsModal.svelte';
  import ManageCitiesModal from './ManageCitiesModal.svelte';
  import EditableTitle from './EditableTitle.svelte';
  import NearbyResultsSheet from './NearbyResultsSheet.svelte';
  import ShareSheet from './ShareSheet.svelte';
  import PoiDetailSheet from './PoiDetailSheet.svelte';
  import PoiPreviewSheet from './PoiPreviewSheet.svelte';
  import SurpriseReveal from './SurpriseReveal.svelte';
  import LocationBlockedHelp from './LocationBlockedHelp.svelte';
  import CitiesRow from './CitiesRow.svelte';
  import BrandMark from './BrandMark.svelte';
  import AddCityModal from './AddCityModal.svelte';
  import AuthModal from './AuthModal.svelte';
  import ConfirmDialog from './ConfirmDialog.svelte';
  import EmptyTripPrompt from './EmptyTripPrompt.svelte';
  import ImportFromUrlModal from './ImportFromUrlModal.svelte';
  import RouteView from './RouteView.svelte';
  import type { RouteStop, RouteMode } from '~/shared/api.js';
  import {
    fetchTrip,
    fetchPois,
    fetchGroups,
    fetchCities,
    deletePoi,
    setPoiGroupMembership,
    deleteCity,
    setActiveCity,
    updateTrip,
    deleteTrip,
    saveTripToDashboard,
    forkTrip,
    fetchPlacePreview,
    bookmarkPlace,
    textSearch,
    newSessionToken,
    setPlacesLanguage,
  } from './api';
  import { resolveLanguage } from './language-resolver';
  import { isOpenNow } from './opening-hours';
  import {
    surpriseMe,
    closestToMe,
    nearThis,
    nearAnyOf,
    DEFAULT_NEARBY_RADIUS_KM,
  } from '~/shared/group-actions.js';
  import { isInViewport } from '~/shared/bias.js';
  import { groupByCity, groupByCategory } from '~/shared/auto-groups.js';
  import type { AutoGroup } from '~/shared/auto-groups.js';
  import LocationPrimer from './LocationPrimer.svelte';
  import { primePermissionState, requestLocation } from './location-service.svelte';
  import { GROUP_TINT_HEX } from '~/shared/api.js';
  import { tick, untrack } from 'svelte';
  import type {
    TripView,
    PoiView,
    GroupView,
    TripCityView,
    ViewportRect,
    PlacePreview,
    TextSearchResult,
  } from '~/shared/api.js';

  let { token }: { token: string } = $props();

  type FetchState =
    | { status: 'loading' }
    | { status: 'notFound' }
    | { status: 'error'; message: string }
    | { status: 'ready'; trip: TripView };

  let fetchState: FetchState = $state({ status: 'loading' });
  let pois: PoiView[] = $state([]);
  let groups: GroupView[] = $state([]);
  let cities: TripCityView[] = $state([]);
  let activeCityId: number | null = $state(null);

  // Prime the geolocation permission state once on mount so click
  // handlers can read it synchronously and call getCurrentPosition
  // inside their user-activation window (critical on Safari).
  $effect(() => {
    void primePermissionState();
  });

  $effect(() => {
    Promise.all([
      fetchTrip(token),
      fetchPois(token).catch(() => [] as PoiView[]),
      fetchGroups(token).catch(() => [] as GroupView[]),
      fetchCities(token).catch(() => [] as TripCityView[]),
    ])
      .then(([trip, poiList, groupList, cityList]) => {
        if (trip === null) {
          fetchState = { status: 'notFound' };
          return;
        }
        pois = poiList;
        groups = groupList;
        cities = cityList;
        activeCityId = trip.activeCityId;
        fetchState = { status: 'ready', trip };
      })
      .catch((e: unknown) => {
        fetchState = {
          status: 'error',
          message: e instanceof Error ? e.message : 'Could not load this trip',
        };
      });
  });

  const activeCity = $derived(
    activeCityId === null ? null : cities.find((c) => c.id === activeCityId) ?? null,
  );

  $effect(() => {
    if (fetchState.status === 'ready') {
      document.title = `${fetchState.trip.name} • Lazy Traveler`;
    }
  });

  async function onTripRename(next: string) {
    const updated = await updateTrip(token, { name: next });
    if (fetchState.status === 'ready') {
      fetchState = { status: 'ready', trip: updated };
    }
  }

  async function onLinkEditChange(next: boolean) {
    const updated = await updateTrip(token, { linkEditEnabled: next });
    if (fetchState.status === 'ready') {
      fetchState = { status: 'ready', trip: updated };
    }
  }

  async function onToggleCover(poiId: number) {
    if (fetchState.status !== 'ready') return;
    const wasCover = fetchState.trip.coverPoiId === poiId;
    const updated = await updateTrip(token, {
      coverPoiId: wasCover ? null : poiId,
    });
    fetchState = { status: 'ready', trip: updated };
  }

  let view = $state<'list' | 'map' | 'auto' | 'route'>('list');
  /** Locality-derived city focus — populated when the user picks a
   *  locality auto-city from the Auto view (e.g. "Nagoya") that isn't
   *  yet a real trip city. Mutually exclusive with `activeCityId`. */
  let activeLocality: string | null = $state(null);
  let mapBounds: ViewportRect | null = $state(null);
  let searchInput:
    | { focus(): void; rotateSession(): void; clearQuery(): void }
    | undefined = $state(undefined);
  let mapView:
    | {
        fitToPois(): void;
        currentBounds(): ViewportRect | null;
        openDiscoveryPopupForResult(result: TextSearchResult): void;
        focusPoi(poiId: number): void;
      }
    | undefined = $state(undefined);

  // Discovery is now implicit: in map view, any non-empty query enters
  // "discovery mode" — autocomplete is suppressed, filters are
  // snapshotted/cleared, the pill blinks when the search is dirty
  // (query or viewport differs from what was last committed), and
  // clicking the pill commits.
  //
  // `liveQuery` is bound from SearchInput so we can react to it here.
  // `committedQuery` records what we last text-searched, paired with
  // `userMovedSinceCommit` to drive the pill-dirty signal.
  let liveQuery = $state('');
  let committedQuery: string | null = $state(null);
  // Snapshots of the value filters at the last commit — drive the
  // area-pill dirty state when the user toggles without re-searching.
  let committedMinRating: number | null = $state(null);
  let committedOpenNow: boolean = $state(false);
  // Set by MapView when the user pans/zooms (dragend/zoom_changed, NOT
  // generic idle — idle also fires on layout-driven resizes like the
  // sidebar appearing after a commit, which would cause a spurious
  // "dirty" blink immediately after a successful search).
  let userMovedSinceCommit = $state(false);
  let discoveryResults: TextSearchResult[] = $state([]);
  let discoverySearching = $state(false);
  let discoveryError: string | null = $state(null);

  // Focus state — the placeId of the POI currently surfaced in the
  // sheet (mobile) or popup (desktop). Drives the active-pin
  // highlight on the map and the sidebar's scroll-into-view sync.
  let focusedPlaceId: string | null = $state(null);
  let bookmarkInFlight = $state(false);

  /** Run the callback against the matchMedia breakpoint that decides
   *  drawer (mobile) vs sidebar (desktop). Synchronous read — fine for
   *  one-off dispatch decisions at click time. */
  function isMobileViewport(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    );
  }

  // True only once a search has been committed — i.e., results exist
  // (or a 0-result commit happened). Typing without committing leaves
  // discoveryActive=false, so filter rows stay visible and the map
  // layout stays stable. This avoids a layout-shift on the first
  // keystroke that was knocking focus off the input.
  const discoveryActive = $derived(view === 'map' && committedQuery !== null);

  // Results visible in the drawer + as discovery pins on the map are
  // deduped against currently-bookmarked POIs (live, not the stale
  // `alreadyBookmarked` flag from the cached text-search response).
  const visibleDiscoveryResults = $derived.by(() => {
    if (!discoveryActive) return [] as TextSearchResult[];
    const bookmarkedPlaceIds = new Set(pois.map((p) => p.placeId));
    return discoveryResults.filter(
      (r) => !r.alreadyBookmarked && !bookmarkedPlaceIds.has(r.placeId),
    );
  });

  // Pill is "dirty" — needs a commit — when there's a query that
  // hasn't been searched yet (including the very first commit where
  // committedQuery is still null), or the user has panned/zoomed
  // since the last search. Both signals are user-initiated (no false
  // positives from layout resizes). Gates on raw conditions rather
  // than `discoveryActive` because dirty must be true *before* the
  // first commit too.
  const discoveryDirty = $derived.by(() => {
    if (view !== 'map') return false;
    const q = liveQuery.trim();
    if (q === '') return false;
    if (q !== committedQuery) return true;
    if (userMovedSinceCommit) return true;
    if (extraMinRating !== committedMinRating) return true;
    if (extraOpenNow !== committedOpenNow) return true;
    return false;
  });
  const discoveryHint = $derived.by<string | null>(() => {
    // Hint shows on the map for transient states the drawer/sidebar
    // can't surface: an in-flight search, an error, or a committed
    // search that returned nothing (no drawer/sidebar in that case).
    // Process-state driven — independent of discoveryActive — so
    // "Searching…" still appears during the very first commit (when
    // committedQuery is still null in-flight).
    if (discoveryError) return discoveryError;
    if (discoverySearching) return 'Searching…';
    if (committedQuery !== null && discoveryResults.length === 0) {
      return 'Nothing in this area. Try a different query or pan the map.';
    }
    return null;
  });

  function onMapPan(v: ViewportRect) {
    mapBounds = v;
  }
  /** Fired by MapView on user-initiated pan/zoom (dragend, zoom_changed) —
   *  distinct from onMapPan, which fires on every idle (including
   *  layout-driven resizes). Used only to mark the pill as dirty. */
  function onMapUserMove() {
    if (discoveryActive && committedQuery !== null) {
      userMovedSinceCommit = true;
    }
  }
  $effect(() => {
    // Live map bounds are only meaningful while the user is in map view.
    if (view !== 'map' && mapBounds !== null) mapBounds = null;
  });

  // Clean up discovery state when the user empties the query (via ×,
  // backspace, or the post-bookmark clearQuery). View switches do NOT
  // clear it — switching to list view and back preserves the sidebar
  // results and the focused POI so users can pick up where they left
  // off. Filter values are never mutated by entering/leaving discovery
  // (filteredPois just ignores them when discoveryActive), so they
  // survive the round-trip automatically.
  $effect(() => {
    if (liveQuery !== '') return;
    untrack(() => {
      if (committedQuery === null && discoveryResults.length === 0) return;
      discoveryResults = [];
      discoverySearching = false;
      discoveryError = null;
      committedQuery = null;
      committedMinRating = null;
      committedOpenNow = false;
      userMovedSinceCommit = false;
      focusedPlaceId = null;
    });
  });

  /** Click handler for the pill. Reads live map bounds + current query,
   *  fires text-search, marks (query, rect) as committed on success.
   *  Note: gates on raw prerequisites (view + query + map bounds)
   *  rather than `discoveryActive` — discoveryActive now means "we
   *  have committed results," which is false on the very first
   *  commit. */
  async function commitDiscoverySearch() {
    if (view !== 'map') return;
    if (fetchState.status !== 'ready') return;
    const rect = mapView?.currentBounds();
    if (!rect) return;
    const query = liveQuery.trim();
    if (query === '') return;
    discoverySearching = true;
    discoveryError = null;
    try {
      const results = await textSearch(fetchState.trip.token, query, {
        viewport: rect,
        // Discovery is "search THIS area" — hard-filter to the rect.
        // With a soft bias, Google returns globally-popular places for
        // generic queries like "restaurant" that ignore the viewport.
        restrict: true,
        minRating: extraMinRating ?? undefined,
        openNow: extraOpenNow || undefined,
      });
      discoveryResults = results;
      committedQuery = query;
      committedMinRating = extraMinRating;
      committedOpenNow = extraOpenNow;
      userMovedSinceCommit = false;
      // New results = fresh start: clear any prior focus so the map
      // doesn't keep a stale POI highlighted from the previous query.
      focusedPlaceId = null;
    } catch (e) {
      discoveryError = e instanceof Error ? e.message : 'Search failed';
    } finally {
      discoverySearching = false;
    }
  }
  type GroupRef =
    | { kind: 'user'; id: number }
    | { kind: 'auto'; key: string }
    | null;
  let selectedGroupRef: GroupRef = $state<GroupRef>(null);
  type GroupModalMode =
    | { kind: 'closed' }
    | { kind: 'create'; afterCreate?: (group: GroupView) => Promise<void> }
    | { kind: 'edit'; group: GroupView };
  let groupModalMode: GroupModalMode = $state({ kind: 'closed' });
  let editGroupsForPoi: PoiView | null = $state(null);
  let detailPoiId: number | null = $state(null);
  const detailPoi = $derived(
    detailPoiId === null ? null : pois.find((p) => p.id === detailPoiId) ?? null,
  );

  type AddCityState = { kind: 'closed' } | { kind: 'open'; initialQuery?: string };
  let addCityState: AddCityState = $state({ kind: 'closed' });
  let importUrlOpen: boolean = $state(false);
  let signInOpen: boolean = $state(false);
  let manageGroupsOpen: boolean = $state(false);
  let manageCitiesOpen: boolean = $state(false);
  let unmappedSuggestion: { name: string } | null = $state(null);
  let groupAddedNotice: { poi: PoiView; group: GroupView } | null = $state(null);
  type DeleteTripState = { kind: 'closed' } | { kind: 'confirming' } | { kind: 'deleting' };
  let deleteTripState: DeleteTripState = $state<DeleteTripState>({ kind: 'closed' });
  // ── Chained action state (save-to-dashboard / fork) ───────────────
  // Both flows share the same shape: optional auth gate before the
  // mutation, in-flight flag during it. Modeled as a single
  // discriminated union so new actions can be added without
  // re-introducing the two-flag pattern.
  type ChainedAction = 'save' | 'fork';
  type ChainedActionState =
    | { kind: 'idle' }
    | { kind: 'awaitingAuth'; action: ChainedAction }
    | { kind: 'running'; action: ChainedAction };
  let chainedAction: ChainedActionState = $state({ kind: 'idle' });

  // ── Route view state ──────────────────────────────────────────────
  let routeStops: RouteStop[] = $state([]);
  let routeMode: RouteMode = $state('walking');
  let clearRouteConfirmOpen: boolean = $state(false);
  function addPoiToRoute(poiId: number) {
    if (routeStops.some((s) => s.kind === 'poi' && s.poiId === poiId)) {
      // Already in the route — just bring the user to it; no duplicate.
      view = 'route';
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      return;
    }
    if (routeStops.length === 0) {
      // First POI: bootstrap as a "get me there from here" route by
      // prepending the location stop. User can remove "here" if they
      // just want to locate the POI on the map instead.
      routeStops = [{ kind: 'here' }, { kind: 'poi', poiId }];
    } else {
      routeStops = [...routeStops, { kind: 'poi', poiId }];
    }
    view = 'route';
    // Reset scroll — the browser preserves the caller view's scroll
    // position across the DOM swap, which on mobile drops the user
    // mid-picker instead of at the route panel they're expecting.
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }

  function clearRoute() {
    routeStops = [];
    clearRouteConfirmOpen = false;
  }

  const poiIdSet = $derived(new Set(pois.map((p) => p.id)));
  const realRouteStopCount = $derived(
    routeStops.filter((s) => s.kind === 'poi' && poiIdSet.has(s.poiId)).length,
  );
  const routeEmpty = $derived(routeStops.length === 0);
  // Onboarding gate. A brand-new editable trip has no city, no POI,
  // and the user is allowed to add things. We replace the whole main
  // body with a single "Where are you going?" prompt instead of the
  // usual silent empty state.
  const isFirstRun = $derived.by(() => {
    if (fetchState.status !== 'ready') return false;
    return fetchState.trip.editable && cities.length === 0 && pois.length === 0;
  });
  // Resolved map / Places API language. Computed once after trip data
  // loads, then locked in (the Maps JS script can't switch language
  // without a page reload — see maps-loader). Toggling map_label_mode
  // PATCHes the trip and reloads, picking up the new resolved value.
  const mapLanguage = $derived.by<string | null>(() => {
    if (fetchState.status !== 'ready') return null;
    return resolveLanguage({
      mode: fetchState.trip.mapLabelMode,
      activeCity: activeCity ?? null,
      cities,
      pois,
      navigatorLanguage: typeof navigator !== 'undefined' ? navigator.language : 'en',
    });
  });
  $effect(() => {
    setPlacesLanguage(mapLanguage);
  });
  const routePoiIds = $derived(
    new Set(
      routeStops
        .filter((s): s is { kind: 'poi'; poiId: number } => s.kind === 'poi')
        .map((s) => s.poiId),
    ),
  );

  async function executeChainedAction(action: ChainedAction) {
    if (fetchState.status !== 'ready') return;
    chainedAction = { kind: 'running', action };
    try {
      if (action === 'save') {
        const { user } = await saveTripToDashboard(fetchState.trip.token);
        window.location.assign(`/u/${user.token}`);
      } else {
        const { token } = await forkTrip(fetchState.trip.token);
        window.location.assign(`/t/${token}`);
      }
    } catch (e) {
      console.error(`Failed to ${action} trip:`, e);
      chainedAction = { kind: 'idle' };
    }
  }

  function requestChainedAction(action: ChainedAction) {
    if (chainedAction.kind === 'running' || fetchState.status !== 'ready') return;
    if (fetchState.trip.viewerUserId === null) {
      // Anonymous visitor — open OTP auth and chain the mutation on
      // verify so first-time users convert in one motion.
      chainedAction = { kind: 'awaitingAuth', action };
      return;
    }
    void executeChainedAction(action);
  }

  async function onConfirmDeleteTrip() {
    if (fetchState.status !== 'ready' || deleteTripState.kind === 'deleting') return;
    deleteTripState = { kind: 'deleting' };
    try {
      await deleteTrip(fetchState.trip.token);
      window.location.assign('/');
    } catch (e) {
      console.error('Failed to delete trip:', e);
      deleteTripState = { kind: 'closed' };
    }
  }

  const canSaveToDashboard = $derived.by(() => {
    if (fetchState.status !== 'ready') return false;
    return fetchState.trip.ownerId === null;
  });
  /** Fork is a visitor action: only meaningful when the trip is owned
   *  by someone *else*. Anonymous trips use Save-to-dashboard (claims
   *  the existing trip); owned trips owned by *us* don't need fork. */
  const canFork = $derived.by(() => {
    if (fetchState.status !== 'ready') return false;
    if (fetchState.trip.ownerId === null) return false;
    return fetchState.trip.ownerId !== fetchState.trip.viewerUserId;
  });
  const runningSave = $derived.by(() => {
    return chainedAction.kind === 'running' && chainedAction.action === 'save';
  });
  const runningFork = $derived.by(() => {
    return chainedAction.kind === 'running' && chainedAction.action === 'fork';
  });
  /** True when the viewer can change trip-level configuration (name,
   *  cover, map labels). Owner-only on owned trips; URL-holder on
   *  anonymous trips. Mirrors the server's PATCH gate. */
  const canEditTripConfig = $derived.by(() => {
    if (fetchState.status !== 'ready') return false;
    if (!fetchState.trip.editable) return false;
    if (fetchState.trip.ownerId === null) return true; // anonymous trip
    return fetchState.trip.ownerId === fetchState.trip.viewerUserId;
  });

  /** Language the trip would resolve to under each mode, so the menu
   *  can label the toggle with the alternative's native-script name
   *  ("Show map in 日本語" / "Show map in English"). When the two
   *  modes resolve to the same code, the toggle is hidden — there's
   *  no meaningful switch (e.g., US user on a US trip). */
  const labelToggle = $derived.by<{ label: string; nextMode: 'local' | 'mine' } | null>(() => {
    if (fetchState.status !== 'ready') return null;
    const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
    const baseInput = {
      activeCity: activeCity ?? null,
      cities,
      pois,
      navigatorLanguage: nav,
    };
    const localLang = resolveLanguage({ ...baseInput, mode: 'local' });
    const mineLang = resolveLanguage({ ...baseInput, mode: 'mine' });
    if (localLang === mineLang) return null;
    const currentMode = fetchState.trip.mapLabelMode;
    const nextMode: 'local' | 'mine' = currentMode === 'mine' ? 'local' : 'mine';
    const nextLang = nextMode === 'mine' ? mineLang : localLang;
    let nativeName = nextLang;
    try {
      const display = new Intl.DisplayNames([nextLang], { type: 'language' }).of(nextLang);
      if (display) nativeName = display;
    } catch {
      // Intl.DisplayNames unavailable — fall back to the bare code.
    }
    return { label: `Show map in ${nativeName}`, nextMode };
  });

  async function setMapLabelMode(next: 'local' | 'mine') {
    if (fetchState.status !== 'ready') return;
    try {
      await updateTrip(fetchState.trip.token, { mapLabelMode: next });
      // Google Maps JS locks language at script-load time and our
      // POI data is fetched with the trip's languageCode — a reload
      // is the simplest correct refresh.
      window.location.reload();
    } catch (e) {
      console.error('Failed to switch map label mode:', e);
    }
  }

  const tripMenuItems = $derived.by<ActionItem[]>(() => {
    if (fetchState.status !== 'ready') return [];
    const items: ActionItem[] = [];
    // Standalone sign-in for viewers without a session. Save/fork
    // also auth-gate internally, but this lets a returning owner
    // (lost their cookie, fresh device) reclaim their session
    // without being forced into a save or fork side-effect first.
    // After verify, AuthModal reloads — the trip refetches with the
    // new session, and ownerId === viewerUserId surfaces owner UI.
    if (fetchState.trip.viewerUserId === null) {
      items.push({
        kind: 'button',
        label: 'Sign in',
        onClick: () => (signInOpen = true),
      });
    }
    if (canEditTripConfig && labelToggle) {
      const { label, nextMode } = labelToggle;
      items.push({
        kind: 'button',
        label,
        onClick: () => void setMapLabelMode(nextMode),
      });
    }
    if (canSaveToDashboard) {
      items.push({
        kind: 'button',
        label: runningSave ? 'Saving…' : 'Save trip',
        disabled: runningSave,
        onClick: () => requestChainedAction('save'),
      });
    }
    if (canFork) {
      items.push({
        kind: 'button',
        label: runningFork ? 'Making a copy…' : 'Make a copy',
        disabled: runningFork,
        onClick: () => requestChainedAction('fork'),
      });
    }
    if (fetchState.trip.editable) {
      items.push({
        kind: 'button',
        label: 'Delete trip',
        destructive: true,
        disabled: deleteTripState.kind === 'deleting',
        onClick: () => (deleteTripState = { kind: 'confirming' }),
      });
    }
    return items;
  });

  const autoCityGroups = $derived(groupByCity(pois, cities));
  const autoCategoryGroups = $derived(groupByCategory(pois));
  const autoGroups = $derived([...autoCityGroups, ...autoCategoryGroups]);

  const selectedUserGroup = $derived.by(() => {
    const r = selectedGroupRef;
    if (r?.kind !== 'user') return null;
    return groups.find((g) => g.id === r.id) ?? null;
  });
  const selectedAutoGroup = $derived.by(() => {
    const r = selectedGroupRef;
    if (r?.kind !== 'auto') return null;
    return autoGroups.find((a) => a.key === r.key) ?? null;
  });
  // Pin tint matches the active filter's color (user or auto). Falls back to
  // brand primary when nothing is filtered.
  const selectedTint = $derived(selectedUserGroup?.tint ?? selectedAutoGroup?.tint ?? null);
  // Members of the active group filter (user OR auto). Used by the filter chain.
  const selectedMemberIds = $derived.by(() => {
    if (selectedUserGroup) {
      return new Set(
        pois.filter((p) => p.groupIds.includes(selectedUserGroup.id)).map((p) => p.id),
      );
    }
    if (selectedAutoGroup) {
      return new Set(selectedAutoGroup.members.map((p) => p.id));
    }
    return null;
  });

  type RevealState =
    | null
    | {
        kind: 'surprise';
        sourceLabel: string;
        members: PoiView[];
        poi: PoiView;
        previous: number[];
      }
    | {
        kind: 'closest';
        sourceLabel: string;
        poi: PoiView;
        distanceM: number;
        originLabel: string;
        locationBlocked: boolean;
      }
    | {
        kind: 'nearby';
        sourceLabel: string;
        poi: PoiView;
        distanceM: number;
      };
  let reveal: RevealState = $state(null);

  type NearbyState =
    | { kind: 'closed' }
    | { kind: 'group'; sourceLabel: string; members: PoiView[] }
    | { kind: 'poi'; poi: PoiView };
  let nearby: NearbyState = $state<NearbyState>({ kind: 'closed' });

  let sharing = $state(false);

  const nearbyResults = $derived.by(() => {
    const n = nearby;
    if (n.kind === 'group') {
      // "Near any POI in the group" — handles multi-city groups (e.g.
      // "Cafés" spanning Taipei + Tokyo) where a centroid would land
      // somewhere meaningless between clusters.
      const memberIds = new Set(n.members.map((p) => p.id));
      return nearAnyOf(n.members, pois, DEFAULT_NEARBY_RADIUS_KM, (p) => !memberIds.has(p.id));
    }
    if (n.kind === 'poi') {
      const sourceId = n.poi.id;
      return nearThis(n.poi, pois, DEFAULT_NEARBY_RADIUS_KM, (p) => p.id !== sourceId);
    }
    return [];
  });

  const nearbyLabels = $derived.by(() => {
    const n = nearby;
    if (n.kind === 'group') {
      return {
        title: `Near "${n.sourceLabel}"`,
        subtitle: `Within ${DEFAULT_NEARBY_RADIUS_KM} km of any place in "${n.sourceLabel}"`,
      };
    }
    if (n.kind === 'poi') {
      return {
        title: `Near ${n.poi.customName ?? n.poi.name}`,
        subtitle: `Within ${DEFAULT_NEARBY_RADIUS_KM} km`,
      };
    }
    return { title: '', subtitle: '' };
  });
  type GeoError =
    | { reason: 'denied'; message: string }
    | { reason: 'unsupported'; message: string }
    | { reason: 'unavailable'; message: string };
  let geoError: GeoError | null = $state(null);

  // Two filter modes.
  // - Discovery (map view + non-empty query): only the current map
  //   viewport matters. City / locality / group filters are ignored
  //   without being mutated — they remain set, just visually hidden
  //   and inactive, so they're still in place when the user exits
  //   discovery (clear query) or switches back from list view.
  // - Normal: city + locality + group filters compose (intersection).
  //   activeCity is a relational filter on poi.cityId (set at bookmark
  //   time, or via the detail sheet's city picker) so a Hakone POI
  //   tagged under Tokyo still appears under the Tokyo filter.
  // In-memory only — filter state doesn't survive reload.
  const RATING_CYCLE: readonly (number | null)[] = [null, 3.5, 4.0, 4.5];
  let extraMinRating: number | null = $state(null);
  let extraOpenNow: boolean = $state(false);
  function cycleMinRating() {
    const i = RATING_CYCLE.indexOf(extraMinRating);
    extraMinRating = RATING_CYCLE[(i + 1) % RATING_CYCLE.length] ?? null;
  }

  const filteredPois = $derived.by(() => {
    if (discoveryActive) {
      const rect = mapBounds;
      if (!rect) return [] as PoiView[];
      return pois.filter((p) => isInViewport(p, rect));
    }
    const cityId = activeCityId;
    const locality = activeLocality;
    const memberIds = selectedMemberIds;
    const minRating = extraMinRating;
    const openNowReq = extraOpenNow;
    const evalAt = openNowReq ? new Date() : null;
    return pois.filter((p) => {
      if (cityId !== null && p.cityId !== cityId) return false;
      if (locality !== null && p.locality !== locality) return false;
      if (memberIds !== null && !memberIds.has(p.id)) return false;
      if (minRating !== null && (p.rating ?? 0) < minRating) return false;
      if (openNowReq) {
        // null period data → treat as "unknown" → exclude when the
        // user explicitly asked for "open now." Better to hide a
        // maybe-closed place than show one. Lazy-refresh backfills
        // periods over time so this set shrinks.
        if (isOpenNow(p.openingPeriods, p.utcOffsetMinutes, evalAt!) !== true) {
          return false;
        }
      }
      return true;
    });
  });

  const groupCounts = $derived.by(() => {
    const counts = new Map<number, number>();
    for (const p of pois) {
      for (const id of p.groupIds) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  });

  const placesLabel = $derived(
    filteredPois.length < pois.length
      ? `${filteredPois.length} of ${pois.length} bookmarked`
      : `${pois.length} bookmarked`,
  );

  function pickGroupFilter(ref: GroupRef) {
    // Skip the no-op refit when the ref hasn't changed — re-clicking the
    // active chip would otherwise yank the camera (filter computes the
    // same set, but fitToPois fires anyway).
    if (groupRefsEqual(ref, selectedGroupRef)) return;
    selectedGroupRef = ref;
    refitMapAfterFilterChange();
  }

  function groupRefsEqual(a: GroupRef, b: GroupRef): boolean {
    if (a === b) return true;
    if (a === null || b === null) return false;
    if (a.kind === 'user' && b.kind === 'user') return a.id === b.id;
    if (a.kind === 'auto' && b.kind === 'auto') return a.key === b.key;
    return false;
  }

  function clearAllFilters() {
    selectedGroupRef = null;
    activeLocality = null;
    extraMinRating = null;
    extraOpenNow = false;
    if (activeCityId !== null) onCityActivate(null);
    refitMapAfterFilterChange();
  }

  function refitMapAfterFilterChange() {
    // Wait a microtask so $derived filteredPois reflects the new state
    // before MapView reads it for fitBounds.
    if (view !== 'map') return;
    queueMicrotask(() => mapView?.fitToPois());
  }

  // Two-step bookmark: search → preview sheet → confirm. Lives here
  // because the preview's photo + address often answers "wait, is this
  // actually the place I meant?" before we commit a row to the trip.
  type PreviewState =
    | { kind: 'closed' }
    | { kind: 'loading'; placeId: string; sessionToken: string; displayName: string }
    | {
        kind: 'open';
        preview: PlacePreview;
        sessionToken: string;
        displayName: string;
        busy: boolean;
        error: string | null;
        /** Discovery path: confirm runs the discovery-specific bookmark
         *  logic (no auto-join, smart filter drop, focusPoi). Close
         *  doesn't refocus the input (user is on the map, not searching).
         *  Defaults to false (autocomplete flow). */
        discoveryFlow?: boolean;
      };
  let previewState: PreviewState = $state({ kind: 'closed' });

  /** Open the preview sheet directly with a TextSearchResult — no
   *  fetchPlacePreview call needed because text-search already
   *  returned every field PlacePreview surfaces. */
  function openDiscoveryPreview(result: TextSearchResult) {
    previewState = {
      kind: 'open',
      preview: {
        placeId: result.placeId,
        name: result.name,
        category: result.category,
        lat: result.lat,
        lng: result.lng,
        rating: result.rating,
        photoReference: result.photoReference,
        formattedAddress: result.formattedAddress,
        // text-search doesn't include hours (Pro-tier field — adding it
        // there would bump every discovery query to Pro pricing). The
        // sheet hides the section when null, so this is fine.
        openingHours: null,
        openingPeriods: null,
        utcOffsetMinutes: null,
        alreadyBookmarked: result.alreadyBookmarked,
      },
      sessionToken: newSessionToken(),
      displayName: result.name,
      busy: false,
      error: null,
      discoveryFlow: true,
    };
  }

  /** Pin click on the map OR row click in the drawer/sidebar. We
   *  dispatch based on viewport: on desktop the in-map popup is the
   *  detail surface; on mobile the popup is hidden behind the drawer
   *  anyway, so we flip the drawer into detail mode and skip the
   *  popup entirely. */
  function onDiscoveryPick(result: TextSearchResult) {
    focusedPlaceId = result.placeId;
    if (isMobileViewport()) {
      // Mobile: open the same sheet as the autocomplete flow. Bigger
      // hero photo, consistent UX with list view.
      openDiscoveryPreview(result);
    } else {
      mapView?.openDiscoveryPopupForResult(result);
    }
  }

  function onPickBookmarkRow(poi: PoiView) {
    // focusPoi opens the popup, which fires onPoiSelect → focusedPlaceId.
    // No manual assignment needed.
    mapView?.focusPoi(poi.id);
  }

  /** "Add to trip" pressed inside the discovery popup. Books the place,
   *  exits discovery (clears the input — filters are already cleared in
   *  the new model), and once the new bookmark marker has rendered,
   *  centers + opens its bookmark popup so the user sees their save. */
  async function onDiscoveryBookmark(result: TextSearchResult) {
    if (fetchState.status !== 'ready') return;
    bookmarkInFlight = true;
    try {
      const { poi, unmappedLocality } = await bookmarkPlace(
        fetchState.trip.token,
        result.placeId,
        newSessionToken(),
        result.name,
      );
      await onBookmarked(poi, unmappedLocality, { autoJoinUserGroup: false });
      // Discovery is exploration, not curation — the new POI doesn't
      // belong to whatever group/area the user was filtered to. Drop
      // filters that would hide it:
      //  - city / locality: drop if the POI doesn't match
      //  - auto-groups: drop unconditionally (computed slice; no
      //    guarantee the new POI is in it)
      //  - user group: drop unconditionally (we suppressed the
      //    auto-join above, so the new POI isn't in the group)
      if (activeCityId !== null && poi.cityId !== activeCityId) {
        activeCityId = null;
      }
      if (activeLocality !== null && poi.locality !== activeLocality) {
        activeLocality = null;
      }
      selectedGroupRef = null;
      // Clearing the query exits discovery; the cleanup effect wipes
      // discovery state, and filteredPois flips back to applying
      // whatever filters survived.
      searchInput?.clearQuery();
      searchInput?.rotateSession();
      // Wait one tick so MapView re-renders its bookmark markers from
      // the updated pois set before we try to open one.
      await tick();
      mapView?.focusPoi(poi.id);
    } catch (e) {
      console.error('Failed to bookmark discovery result:', e);
    } finally {
      bookmarkInFlight = false;
    }
  }

  async function onPickPlace(placeId: string, sessionToken: string, displayName: string) {
    if (fetchState.status !== 'ready') return;
    previewState = { kind: 'loading', placeId, sessionToken, displayName };
    try {
      const preview = await fetchPlacePreview(fetchState.trip.token, placeId, sessionToken);
      // Defensive: if the user closed the sheet while the fetch was in
      // flight, don't pop it back open.
      const current = previewState;
      if (current.kind === 'loading' && current.placeId === placeId) {
        previewState = {
          kind: 'open',
          preview,
          sessionToken,
          displayName,
          busy: false,
          error: null,
        };
      }
    } catch (e) {
      previewState = { kind: 'closed' };
      // Surface the error somewhere noticeable. The trip page doesn't have
      // a generic toast yet, so we log + leave the search input free to
      // try again.
      console.error('Place preview failed:', e);
    }
  }

  async function confirmBookmarkFromPreview() {
    const current = previewState;
    if (current.kind !== 'open' || fetchState.status !== 'ready') return;
    if (current.preview.alreadyBookmarked) return;
    previewState = { ...current, busy: true, error: null };
    try {
      const { poi, unmappedLocality } = await bookmarkPlace(
        fetchState.trip.token,
        current.preview.placeId,
        current.sessionToken,
        current.displayName,
      );
      if (current.discoveryFlow) {
        // Discovery path: no auto-join, smart filter drop so the
        // new POI is visible after we exit discovery, focusPoi to
        // surface the freshly saved place on the map.
        await onBookmarked(poi, unmappedLocality, { autoJoinUserGroup: false });
        if (activeCityId !== null && poi.cityId !== activeCityId) {
          activeCityId = null;
        }
        if (activeLocality !== null && poi.locality !== activeLocality) {
          activeLocality = null;
        }
        selectedGroupRef = null;
        previewState = { kind: 'closed' };
        searchInput?.clearQuery();
        searchInput?.rotateSession();
        await tick();
        mapView?.focusPoi(poi.id);
      } else {
        // Autocomplete (list-view) path: existing behavior — auto-join
        // active user group, just close the sheet and clear the input.
        await onBookmarked(poi, unmappedLocality);
        previewState = { kind: 'closed' };
        searchInput?.clearQuery();
        searchInput?.rotateSession();
      }
    } catch (e) {
      previewState = {
        ...current,
        busy: false,
        error: e instanceof Error ? e.message : 'Could not bookmark',
      };
    }
  }

  async function onBookmarked(
    poi: PoiView,
    unmappedLocality?: { name: string } | null,
    { autoJoinUserGroup = true }: { autoJoinUserGroup?: boolean } = {},
  ) {
    pois = [poi, ...pois];
    if (unmappedLocality) {
      unmappedSuggestion = unmappedLocality;
    }

    // Auto-group filters are descriptive (category / locality / proximity),
    // so a newly bookmarked POI may simply not match — silently hiding what
    // the user just added is worse than dropping the filter. Clear it.
    // Trip-city focus (activeCityId) and user-group filters compose with
    // bookmarks meaningfully, so they're left alone.
    if (selectedGroupRef?.kind === 'auto') selectedGroupRef = null;
    if (activeLocality !== null) activeLocality = null;

    // List-view path is curation: bookmarking while filtered to a user
    // group means "add to this bucket." Discovery path is exploration:
    // bookmarking a nearby place doesn't imply it belongs in whatever
    // group the user was browsing — the caller suppresses the auto-join
    // via `autoJoinUserGroup: false` for that case.
    if (!autoJoinUserGroup) return;
    const target = selectedUserGroup;
    if (target && fetchState.status === 'ready') {
      try {
        const updated = await setPoiGroupMembership(fetchState.trip.token, poi.id, [target.id]);
        pois = pois.map((p) => (p.id === updated.id ? updated : p));
        groupAddedNotice = { poi: updated, group: target };
      } catch (e) {
        console.error('Failed to auto-add to filter group:', e);
      }
    }
  }

  async function undoGroupAdd() {
    if (!groupAddedNotice || fetchState.status !== 'ready') return;
    const { poi, group } = groupAddedNotice;
    groupAddedNotice = null;
    try {
      const fresh = pois.find((p) => p.id === poi.id) ?? poi;
      const updated = await setPoiGroupMembership(
        fetchState.trip.token,
        fresh.id,
        fresh.groupIds.filter((id) => id !== group.id),
      );
      pois = pois.map((p) => (p.id === updated.id ? updated : p));
    } catch (e) {
      console.error('Failed to undo group add:', e);
    }
  }

  async function onCityAdded(city: TripCityView, newActiveCityId: number | null) {
    const wasFirstCity = cities.length === 0;
    cities = [...cities, city];
    activeCityId = newActiveCityId;
    // The new city replaces any locality focus that was the source of the
    // promote action — same mutual exclusion the existing onCityActivate
    // enforces.
    activeLocality = null;
    addCityState = { kind: 'closed' };
    unmappedSuggestion = null;
    // First-city onboarding: auto-name the trip after the city if the
    // user hasn't picked a name yet. Removes the "what should I call
    // this?" overhead. Skips when the user has already renamed (the
    // default "New trip" string is the signal).
    if (
      wasFirstCity &&
      fetchState.status === 'ready' &&
      fetchState.trip.name === 'New trip'
    ) {
      try {
        const updated = await updateTrip(fetchState.trip.token, {
          name: `${city.name} trip`,
        });
        fetchState = { status: 'ready', trip: updated };
      } catch (e) {
        console.error('Failed to auto-name trip from first city:', e);
      }
    }
    // Server's city-add backfill rewrote pois.city_id for any null-cityId
    // POIs that fall in the new viewport. Refetch so the client picks up
    // the new associations (auto-city groups and the cityId filter then
    // reflect reality without a page reload).
    if (fetchState.status === 'ready') {
      try {
        pois = await fetchPois(fetchState.trip.token);
      } catch (e) {
        console.error('Failed to refresh POIs after city add:', e);
      }
    }
  }

  async function onCityActivate(cityId: number | null) {
    if (fetchState.status !== 'ready') return;
    const previous = activeCityId;
    activeCityId = cityId;
    // Picking a real trip city overrides any active locality focus
    // (the two are mutually exclusive).
    if (cityId !== null) {
      activeLocality = null;
    }
    refitMapAfterFilterChange();
    // Persist only for editable viewers — the server's PATCH gate would
    // 403 a view-only viewer and revert the optimistic update, making
    // the chip flash active then unselect itself. View-only viewers
    // get a session-local filter instead.
    if (!fetchState.trip.editable) return;
    try {
      await setActiveCity(fetchState.trip.token, cityId);
    } catch (e) {
      activeCityId = previous;
      console.error('Failed to set active city:', e);
    }
  }

  async function deleteCityNow(city: TripCityView) {
    if (fetchState.status !== 'ready') return;
    const previous = { cities, activeCityId };
    cities = cities.filter((c) => c.id !== city.id);
    if (activeCityId === city.id) activeCityId = null;
    try {
      await deleteCity(fetchState.trip.token, city.id);
    } catch (e) {
      cities = previous.cities;
      activeCityId = previous.activeCityId;
      console.error('Failed to delete city:', e);
    }
  }


  function onAcceptUnmappedSuggestion() {
    if (!unmappedSuggestion) return;
    const initialQuery = unmappedSuggestion.name;
    unmappedSuggestion = null;
    addCityState = { kind: 'open', initialQuery };
  }

  function onDismissUnmappedSuggestion() {
    unmappedSuggestion = null;
  }

  function onJumpToPoi(placeId: string) {
    const el = document.querySelector(`[data-place-id="${placeId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  async function onRemovePoi(poi: PoiView) {
    if (fetchState.status !== 'ready') return;
    const previous = pois;
    pois = pois.filter((p) => p.id !== poi.id);
    try {
      await deletePoi(fetchState.trip.token, poi.id);
    } catch (err) {
      pois = previous;
      console.error('Failed to remove POI:', err);
    }
  }

  async function onGroupSaved(group: GroupView) {
    if (groupModalMode.kind === 'create') {
      groups = [...groups, group];
      const after = groupModalMode.afterCreate;
      if (after) {
        try {
          await after(group);
        } catch (e) {
          console.error('Failed afterCreate continuation:', e);
        }
      }
    } else if (groupModalMode.kind === 'edit') {
      groups = groups.map((g) => (g.id === group.id ? group : g));
    }
    groupModalMode = { kind: 'closed' };
  }

  function autoAssignNewGroupTo(poi: PoiView) {
    return async (group: GroupView) => {
      if (fetchState.status !== 'ready') return;
      const updated = await setPoiGroupMembership(fetchState.trip.token, poi.id, [
        ...poi.groupIds,
        group.id,
      ]);
      pois = pois.map((p) => (p.id === updated.id ? updated : p));
      editGroupsForPoi = updated;
    };
  }

  function onGroupDeleted(groupId: number) {
    groups = groups.filter((g) => g.id !== groupId);
    pois = pois.map((p) =>
      p.groupIds.includes(groupId)
        ? { ...p, groupIds: p.groupIds.filter((id) => id !== groupId) }
        : p,
    );
    if (selectedGroupRef?.kind === 'user' && selectedGroupRef.id === groupId) {
      selectedGroupRef = null;
    }
  }

  function onPoiUpdated(updated: PoiView) {
    pois = pois.map((p) => (p.id === updated.id ? updated : p));
    editGroupsForPoi = null;
  }

  /** Normalized payload for group actions — works for both user groups and
   *  auto-groups since all the action handlers need is the label + members. */
  type GroupAction = { sourceLabel: string; members: PoiView[]; ref: GroupRef };

  function actionForUserGroup(group: GroupView): GroupAction {
    return {
      sourceLabel: group.name,
      members: pois.filter((p) => p.groupIds.includes(group.id)),
      ref: { kind: 'user', id: group.id },
    };
  }
  function actionForAutoGroup(auto: AutoGroup): GroupAction {
    return {
      sourceLabel: auto.name,
      members: auto.members,
      ref: { kind: 'auto', key: auto.key },
    };
  }

  function pickSurprise(action: GroupAction, previous: number[] = []) {
    const previousSet = new Set(previous);
    const fresh = action.members.filter((p) => !previousSet.has(p.id));
    const candidates = fresh.length > 0 ? fresh : action.members;
    const pick = surpriseMe(candidates);
    if (!pick) return;
    const wasFresh = fresh.length > 0;
    reveal = {
      kind: 'surprise',
      sourceLabel: action.sourceLabel,
      members: action.members,
      poi: pick,
      previous: wasFresh ? [...previous, pick.id] : [pick.id],
    };
  }

  async function pickClosest(action: GroupAction) {
    geoError = null;

    // location-service caches permission state synchronously, so when it
    // resolves to 'granted' requestLocation calls getCurrentPosition
    // immediately without any intervening await — Safari's
    // user-activation window from this click survives intact. When
    // unprompted, the shared LocationPrimer modal opens first.
    const result = await requestLocation({
      feature: 'finding the closest place to you',
    });

    let origin: { lat: number; lng: number } | null = null;
    let originLabel = '';
    // Treat user-cancelled primer the same as denial for fallback
    // selection (use active city instead).
    const blocked =
      !result.ok && (result.reason === 'denied' || result.reason === 'cancelled');
    if (result.ok) {
      origin = { lat: result.lat, lng: result.lng };
      originLabel = 'your location';
    } else if (activeCity) {
      origin = { lat: activeCity.lat, lng: activeCity.lng };
      originLabel = activeCity.name;
    } else {
      const reason =
        result.reason === 'unsupported'
          ? 'unsupported'
          : blocked
            ? 'denied'
            : 'unavailable';
      const message =
        reason === 'denied'
          ? 'Location is blocked. Enable it in your browser settings, or add a city to this trip and try again.'
          : reason === 'unsupported'
            ? "This browser doesn't support location, and no city is set as origin."
            : 'Location is unavailable, and no city is set as origin. Add a city to this trip and try again.';
      geoError = { reason, message };
      return;
    }

    const closest = closestToMe(action.members, origin);
    if (!closest) return;
    reveal = {
      kind: 'closest',
      sourceLabel: action.sourceLabel,
      poi: closest.poi,
      distanceM: closest.distanceM,
      originLabel,
      locationBlocked: blocked,
    };
  }

  function pickAnother() {
    if (reveal?.kind !== 'surprise') return;
    pickSurprise(
      { sourceLabel: reveal.sourceLabel, members: reveal.members, ref: null },
      reveal.previous,
    );
  }

  function onNearbyForGroup(action: GroupAction) {
    nearby = { kind: 'group', sourceLabel: action.sourceLabel, members: action.members };
  }
  /**
   * Apply a GroupRef as the trip's filter and switch to a target view.
   * Called by "Show on map" / "View list" actions on group cards.
   *
   * These actions are explicit *focus* intents — "show me this group" —
   * so whatever city / locality / map-area filter was active gets cleared.
   * Without this, picking a Tokyo group while Kyoto is the active city
   * would AND the two and leave an empty map, surprising the user.
   *
   * Two auto-group kinds collapse to existing filter sources instead of
   * setting selectedGroupRef:
   *   - tripCity → activeCityId (functionally identical to tapping the
   *     city chip in the Cities row)
   *   - locality → activeLocality (the auto-derived city focus)
   *
   * Composing-with-city behavior still lives on the filter-chip path
   * (`pickGroupFilter`), which is the surface where stacking ("Tokyo +
   * Cafés") is the user's actual intent.
   */
  function applyGroupRef(ref: GroupRef): void {
    if (ref?.kind === 'auto') {
      const auto = autoGroups.find((a) => a.key === ref.key);
      if (auto?.kind === 'tripCity') {
        selectedGroupRef = null;
        activeLocality = null;
        onCityActivate(auto.cityId);
        return;
      }
      if (auto?.kind === 'locality') {
        selectedGroupRef = null;
        if (activeCityId !== null) onCityActivate(null);
        activeLocality = auto.name;
        return;
      }
    }
    // category / proximity auto-groups AND user groups: isolate this
    // group as the sole filter.
    if (activeCityId !== null) onCityActivate(null);
    activeLocality = null;
    selectedGroupRef = ref;
  }

  function onShowOnMap(ref: GroupRef) {
    applyGroupRef(ref);
    view = 'map';
    // The map may not be mounted yet (just switched to map view); the
    // first-render fitBounds will handle this case. If the map is already
    // mounted (re-show from map view), explicitly refit.
    queueMicrotask(() => mapView?.fitToPois());
  }
  function onViewList(ref: GroupRef) {
    applyGroupRef(ref);
    view = 'list';
  }
  function onNearbyForPoi(poi: PoiView) {
    nearby = { kind: 'poi', poi };
  }
  function onPickFromNearby(result: { poi: PoiView; distanceM: number }) {
    const n = nearby;
    const sourceLabel =
      n.kind === 'group' ? n.sourceLabel : n.kind === 'poi' ? n.poi.customName ?? n.poi.name : '';
    nearby = { kind: 'closed' };
    reveal = {
      kind: 'nearby',
      sourceLabel,
      poi: result.poi,
      distanceM: result.distanceM,
    };
  }
</script>

{#if fetchState.status === 'loading'}
  <div class="min-h-screen flex items-center justify-center" style="color: var(--color-muted);">
    Loading…
  </div>
{:else if fetchState.status === 'notFound'}
  <div class="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center">
    <h1 class="text-display-md m-0" style="color: var(--color-ink);">Trip not found</h1>
    <p class="text-body-sm m-0" style="color: var(--color-muted);">
      The link may be wrong or the trip may have been deleted.
    </p>
    <a href="/" class="btn btn-tertiary">← back home</a>
  </div>
{:else if fetchState.status === 'error'}
  <div class="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center">
    <h1 class="text-display-md m-0" style="color: var(--color-ink);">Couldn't load this trip</h1>
    <p class="text-body-sm m-0" style="color: var(--color-muted);">{fetchState.message}</p>
    <a href="/" class="btn btn-tertiary">← back home</a>
  </div>
{:else}
  <div class="min-h-screen flex flex-col" style="background: var(--color-canvas);">
    <header
      class="sticky top-0 z-30"
      style="background: var(--color-canvas); border-bottom: 1px solid var(--color-hairline-soft);"
    >
      <div class="header-bar">
        <div class="header-row">
          {#if fetchState.trip.viewerUserId}
            <a class="back-link" href="/" aria-label="Back to your trips">
              <span aria-hidden="true">←</span>
              <span class="back-label">Your trips</span>
            </a>
          {:else}
            <!-- Signed-out viewers had no way back to landing. The
                 brand-mark link doubles as a home shortcut. -->
            <a class="back-link brand-home" href="/" aria-label="Lazy Traveler home">
              <BrandMark size={22} />
              <span class="back-label">Lazy Traveler</span>
            </a>
          {/if}
          <EditableTitle
            class="flex-1 min-w-0"
            headingClass="trip-name truncate"
            as="h1"
            value={fetchState.trip.name}
            editable={fetchState.trip.editable}
            ariaLabel="Trip name"
            onCommit={onTripRename}
          />

          {#if !isFirstRun}
          <div class="view-toggle desktop-only" role="group" aria-label="View">
            <button
              type="button"
              class="view-toggle__seg {view === 'list' ? 'view-toggle__seg--active' : ''}"
              aria-pressed={view === 'list'}
              onclick={() => (view = 'list')}
            >List</button>
            <button
              type="button"
              class="view-toggle__seg {view === 'map' ? 'view-toggle__seg--active' : ''}"
              aria-pressed={view === 'map'}
              onclick={() => (view = 'map')}
            >Map</button>
            <button
              type="button"
              class="view-toggle__seg {view === 'route' ? 'view-toggle__seg--active' : ''}"
              aria-pressed={view === 'route'}
              onclick={() => (view = 'route')}
            >Route{realRouteStopCount > 0 ? ` · ${realRouteStopCount}` : ''}</button>
            <button
              type="button"
              class="view-toggle__seg {view === 'auto' ? 'view-toggle__seg--active' : ''}"
              aria-pressed={view === 'auto'}
              onclick={() => (view = 'auto')}
            >Auto</button>
          </div>
          {/if}
          <button
            class="icon-circle-sm"
            type="button"
            aria-label="Share trip"
            onclick={() => (sharing = true)}
          >
            <Icon name="share" size={17} />
          </button>
          {#if tripMenuItems.length > 0}
            <ActionsMenu items={tripMenuItems} label="Trip actions" size="md" emphasis="plain" />
          {/if}
        </div>
        {#if !isFirstRun}
        <div class="view-toggle mobile-only" role="group" aria-label="View">
          <button
            type="button"
            class="view-toggle__seg {view === 'list' ? 'view-toggle__seg--active' : ''}"
            aria-pressed={view === 'list'}
            onclick={() => (view = 'list')}
          >List</button>
          <button
            type="button"
            class="view-toggle__seg {view === 'map' ? 'view-toggle__seg--active' : ''}"
            aria-pressed={view === 'map'}
            onclick={() => (view = 'map')}
          >Map</button>
          <button
            type="button"
            class="view-toggle__seg {view === 'route' ? 'view-toggle__seg--active' : ''}"
            aria-pressed={view === 'route'}
            onclick={() => (view = 'route')}
          >Route{realRouteStopCount > 0 ? ` · ${realRouteStopCount}` : ''}</button>
          <button
            type="button"
            class="view-toggle__seg {view === 'auto' ? 'view-toggle__seg--active' : ''}"
            aria-pressed={view === 'auto'}
            onclick={() => (view = 'auto')}
          >Auto</button>
        </div>
        {/if}
      </div>
    </header>

    <main class="max-w-7xl w-full mx-auto px-5 sm:px-10 py-5 sm:py-10 flex-1 flex flex-col gap-8">
      {#if isFirstRun && fetchState.status === 'ready'}
        <EmptyTripPrompt tripToken={fetchState.trip.token} onAdded={onCityAdded} />
      {:else if view === 'route'}
        <CitiesRow
          cities={cities}
          activeCityId={activeCityId}
          activeLocality={activeLocality}
          editable={false}
          onActivate={onCityActivate}
          onClearLocality={() => (activeLocality = null)}
          onAdd={() => (addCityState = { kind: 'open' })}
        />
        {#if pois.length > 0 || groups.length > 0}
          <nav aria-label="Filter by group" class="filter-row filter-row--tight">
            <span class="row-label" aria-hidden="true">
              <Icon name="search" size={12} strokeWidth={2.25} />
              <span>Showing:</span>
            </span>
            <button
              type="button"
              class="filter-chip {selectedGroupRef === null ? 'active' : ''}"
              aria-pressed={selectedGroupRef === null}
              onclick={() => pickGroupFilter(null)}
            >
              All places
            </button>
            {#if selectedAutoGroup}
              <button
                type="button"
                class="filter-chip filter-chip--auto"
                aria-pressed={true}
                title="Auto group ({selectedAutoGroup.kind}) — tap to clear"
                onclick={() => pickGroupFilter(null)}
              >
                <Icon name="sparkles" size={11} strokeWidth={2.25} />
                <span>{selectedAutoGroup.name}</span>
                <Icon name="x" size={11} strokeWidth={2.25} />
              </button>
            {/if}
            {#each groups as g (g.id)}
              {@const isActive = selectedGroupRef?.kind === 'user' && selectedGroupRef.id === g.id}
              <button
                type="button"
                class="filter-chip {isActive ? 'active' : ''}"
                aria-pressed={isActive}
                onclick={() => pickGroupFilter(isActive ? null : { kind: 'user', id: g.id })}
              >
                {#if !isActive}
                  <span class="dot dot--{g.tint}"></span>
                {/if}
                <span>{g.name}</span>
              </button>
            {/each}
          </nav>
        {/if}
        <RouteView
          {pois}
          {filteredPois}
          {cities}
          {routeStops}
          onRouteStopsChange={(next) => (routeStops = next)}
          {routeMode}
          onRouteModeChange={(m) => (routeMode = m)}
          onClearRouteRequested={() => (clearRouteConfirmOpen = true)}
          activeCity={activeCity}
        />
      {:else if view === 'auto'}
        {#if groups.length === 0 && autoGroups.length === 0}
          <div class="flex-1 flex flex-col items-center justify-center gap-3 text-center py-12">
            <h2 class="text-display-md m-0" style="color: var(--color-ink);">Nothing to decide on yet.</h2>
            <p class="text-body m-0" style="color: var(--color-muted);">
              Bookmark a few places, or create a group — surprise / closest / by city / by category will all show up here.
            </p>
          </div>
        {:else}
          {#if groups.length > 0}
            <section class="flex flex-col gap-3">
              <div class="flex items-baseline justify-between">
                <h2 class="text-display-md m-0" style="color: var(--color-ink);">Decide for me</h2>
                <span class="text-body-sm" style="color: var(--color-muted);">
                  {groups.length} {groups.length === 1 ? 'group' : 'groups'}
                </span>
              </div>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {#each groups as g (g.id)}
                  <GroupCard
                    group={g}
                    poiCount={groupCounts.get(g.id) ?? 0}
                    tripToken={fetchState.trip.token}
                    editable={fetchState.trip.editable}
                    onDeleted={onGroupDeleted}
                    onEdit={(grp) => (groupModalMode = { kind: 'edit', group: grp })}
                    onSurprise={(grp) => pickSurprise(actionForUserGroup(grp))}
                    onClosest={(grp) => pickClosest(actionForUserGroup(grp))}
                    onNearby={(grp) => onNearbyForGroup(actionForUserGroup(grp))}
                    onShowOnMap={(grp) => onShowOnMap({ kind: 'user', id: grp.id })}
                  />
                {/each}
              </div>
              {#if geoError}
                <LocationBlockedHelp
                  compact
                  errorMessage={geoError.message}
                  showHelp={geoError.reason === 'denied'}
                />
              {/if}
            </section>
          {/if}
          {#each [
            { label: 'By city', singular: 'city', plural: 'cities', items: autoCityGroups },
            { label: 'By category', singular: 'category', plural: 'categories', items: autoCategoryGroups },
          ] as section (section.label)}
            {#if section.items.length > 0}
              <section class="flex flex-col gap-3">
                <div class="flex items-baseline justify-between">
                  <h2 class="text-display-md m-0" style="color: var(--color-ink);">{section.label}</h2>
                  <span class="text-body-sm" style="color: var(--color-muted);">
                    {section.items.length} {section.items.length === 1 ? section.singular : section.plural}
                  </span>
                </div>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {#each section.items as a (a.key)}
                    <AutoGroupCard
                      group={a}
                      onShowOnMap={(auto) => onShowOnMap({ kind: 'auto', key: auto.key })}
                      onViewList={(auto) => onViewList({ kind: 'auto', key: auto.key })}
                    />
                  {/each}
                </div>
              </section>
            {/if}
          {/each}
        {/if}
      {:else}
      <div class="flex flex-col gap-3">
        {#if fetchState.trip.editable}
          <SearchInput
            bind:this={searchInput}
            bind:query={liveQuery}
            tripToken={fetchState.trip.token}
            editable={fetchState.trip.editable}
            pois={pois}
            activeCity={activeCity}
            hasCities={cities.length > 0}
            autoAddGroup={selectedUserGroup}
            discoveryMode={view === 'map'}
            discoveryDirty={discoveryDirty}
            onPickPlace={onPickPlace}
            onJumpToPoi={onJumpToPoi}
            onAddCity={() => (addCityState = { kind: 'open' })}
            onDiscoverHere={commitDiscoverySearch}
            onImportUrl={() => (importUrlOpen = true)}
          />
        {/if}
        <div class:filters-locked={discoveryActive} aria-hidden={discoveryActive || undefined}>
          <CitiesRow
            cities={cities}
            activeCityId={activeCityId}
            activeLocality={activeLocality}
            editable={fetchState.trip.editable}
            onActivate={onCityActivate}
            onClearLocality={() => (activeLocality = null)}
            onPromoteLocality={() => {
              // Server's city-add backfill retags null-cityId POIs in the
              // new viewport, covering the locality POIs that surfaced this chip.
              if (activeLocality !== null) {
                addCityState = { kind: 'open', initialQuery: activeLocality };
              }
            }}
            onAdd={() => (addCityState = { kind: 'open' })}
            onManage={() => (manageCitiesOpen = true)}
          />
        </div>
        {#if unmappedSuggestion && fetchState.trip.editable}
          <div class="banner banner--accent" role="status">
            <Icon name="map-pin" size={14} />
            <span class="banner__text">
              Add <strong>{unmappedSuggestion.name}</strong> to your trip cities? Search will focus on {unmappedSuggestion.name} places.
            </span>
            <span class="banner__actions">
              <button class="btn btn-tertiary" type="button" onclick={onDismissUnmappedSuggestion}>Dismiss</button>
              <button class="btn btn-primary" type="button" onclick={onAcceptUnmappedSuggestion}>Add</button>
            </span>
          </div>
        {/if}
        {#if groupAddedNotice && fetchState.trip.editable}
          <div class="banner" role="status">
            <span class="dot dot--{groupAddedNotice.group.tint}"></span>
            <span class="banner__text">
              Added <strong>{groupAddedNotice.poi.name}</strong> to <strong>{groupAddedNotice.group.name}</strong>.
            </span>
            <span class="banner__actions">
              <button class="btn btn-tertiary" type="button" onclick={() => (groupAddedNotice = null)}>Dismiss</button>
              <button class="btn btn-secondary" type="button" onclick={undoGroupAdd}>Undo</button>
            </span>
          </div>
        {/if}
      </div>

      {#if pois.length > 0 || groups.length > 0 || discoveryActive}
        <nav aria-label="Filter places" class="filter-row filter-row--tight">
          <span class="row-label" aria-hidden="true">
            <Icon name="search" size={12} strokeWidth={2.25} />
            <span>Showing:</span>
          </span>
          <!-- Membership chips (city/group). Hidden during discovery
               because Google's text-search results aren't filtered by
               those — value chips below take over the refinement role. -->
          <!-- "All places" anchor — universal default. Lives in its
               own .filter-group span so it hides during discovery
               alongside the user/auto group chips below. -->
          <span class="filter-group" class:filters-locked={discoveryActive}>
            <button
              type="button"
              class="filter-chip {selectedGroupRef === null ? 'active' : ''}"
              aria-pressed={selectedGroupRef === null}
              onclick={() => pickGroupFilter(null)}
            >
              All places
            </button>
          </span>
          <span class="filter-values">
            <button
              type="button"
              class="filter-chip {extraMinRating !== null ? 'active' : ''}"
              aria-pressed={extraMinRating !== null}
              title="Tap to cycle: any → 3.5+ → 4.0+ → 4.5+ → any"
              onclick={cycleMinRating}
            >
              <Icon name="star" size={11} />
              <span>{extraMinRating === null ? 'Any rating' : `${extraMinRating.toFixed(1)}+`}</span>
            </button>
            <button
              type="button"
              class="filter-chip {extraOpenNow ? 'active' : ''}"
              aria-pressed={extraOpenNow}
              onclick={() => (extraOpenNow = !extraOpenNow)}
            >
              <Icon name="check" size={11} strokeWidth={2.5} />
              <span>Open now</span>
            </button>
          </span>
          <span class="filter-group" class:filters-locked={discoveryActive}>
            {#if selectedAutoGroup}
              <!-- Currently active auto-group filter (set via the Auto view).
                   Sits right after the value chips so the user's current focus
                   isn't buried after their user groups. Dashed border +
                   sparkles glyph signals "computed slice, tap to clear." -->
              <button
                type="button"
                class="filter-chip filter-chip--auto"
                aria-pressed={true}
                title="Auto group ({selectedAutoGroup.kind}) — tap to clear"
                onclick={() => pickGroupFilter(null)}
              >
                <Icon name="sparkles" size={11} strokeWidth={2.25} />
                <span>{selectedAutoGroup.name}</span>
                <Icon name="x" size={11} strokeWidth={2.25} />
              </button>
            {/if}
            {#each groups as g (g.id)}
              {@const isActive = selectedGroupRef?.kind === 'user' && selectedGroupRef.id === g.id}
              <button
                type="button"
                class="filter-chip {isActive ? 'active' : ''}"
                aria-pressed={isActive}
                onclick={() => pickGroupFilter(isActive ? null : { kind: 'user', id: g.id })}
              >
                {#if !isActive}
                  <span class="dot dot--{g.tint}"></span>
                {/if}
                <span>{g.name}</span>
              </button>
            {/each}
            {#if fetchState.trip.editable && groups.length === 0}
              <button
                type="button"
                class="filter-chip ghost"
                onclick={() => (groupModalMode = { kind: 'create' })}
              >
                <Icon name="plus" size={12} strokeWidth={2} />
                <span>New group</span>
              </button>
            {/if}
            {#if fetchState.trip.editable && groups.length > 0}
              <span class="filter-chip filter-chip--split">
                <button
                  type="button"
                  class="split-action"
                  onclick={() => (groupModalMode = { kind: 'create' })}
                >
                  <Icon name="plus" size={12} strokeWidth={2} />
                  <span>New group</span>
                </button>
                <span class="split-divider" aria-hidden="true"></span>
                <button
                  type="button"
                  class="split-action split-action--icon"
                  aria-label="Manage groups"
                  title="Manage groups"
                  onclick={() => (manageGroupsOpen = true)}
                >
                  <Icon name="settings" size={12} strokeWidth={2} />
                </button>
              </span>
            {/if}
          </span>
        </nav>
      {/if}

      {#if pois.length === 0 && view !== 'map'}
        <!-- Trip-level empty state — list view only. In map view we
             still render the map (centered on the active city) so
             the user can drive the discovery flow against it; the
             map's own empty-state hint guides them. -->
        <div class="flex-1 flex flex-col items-center justify-center gap-3 text-center py-12">
          <h2 class="text-display-md m-0" style="color: var(--color-ink);">No places yet.</h2>
          <p class="text-body m-0" style="color: var(--color-muted);">
            Search above to add your first one.
          </p>
        </div>
      {:else}
        <section class="flex flex-col gap-5">
          <div class="flex items-baseline justify-between gap-3">
            <h2 class="text-display-md m-0" style="color: var(--color-ink);">Places</h2>
            <span class="text-body-sm" style="color: var(--color-muted);">{placesLabel}</span>
          </div>
          {#if view === 'map'}
            <div class="map-stage map-stage--with-sidebar">
              <MapSidebar
                headerLabel={discoveryActive
                  ? visibleDiscoveryResults.length === 1
                    ? `1 result for "${committedQuery ?? ''}"`
                    : `${visibleDiscoveryResults.length} results for "${committedQuery ?? ''}"`
                  : placesLabel}
                bookmarks={discoveryActive ? null : filteredPois}
                discoveryResults={discoveryActive ? visibleDiscoveryResults : null}
                groups={groups}
                focusedPlaceId={focusedPlaceId}
                emptyMessage={discoveryActive
                  ? 'No results yet — type a query and tap "this area".'
                  : pois.length === 0
                    ? 'No bookmarks yet. Search above to add one.'
                    : 'No bookmarks match the current filters.'}
                onPickBookmark={onPickBookmarkRow}
                onPickDiscoveryResult={onDiscoveryPick}
              />
              <MapView
                bind:this={mapView}
                pois={filteredPois}
                pinTint={selectedTint ? GROUP_TINT_HEX[selectedTint] : undefined}
                activeCity={activeCity}
                routeEmpty={routeEmpty}
                routePoiIds={routePoiIds}
                discoveryActive={discoveryActive}
                discoveryResults={discoveryResults}
                discoveryHint={discoveryHint}
                focusedPlaceId={focusedPlaceId}
                mapLanguage={mapLanguage ?? undefined}
                hoursLocale={mapLanguage}
                onDiscoveryPick={onDiscoveryPick}
                onDiscoveryBookmark={onDiscoveryBookmark}
                onPoiOpen={(p) => (detailPoiId = p.id)}
                onPoiSelect={(placeId) => (focusedPlaceId = placeId)}
                onMapPan={onMapPan}
                onMapUserMove={onMapUserMove}
                onAddToRoute={(p) => addPoiToRoute(p.id)}
              />
            </div>
          {:else if filteredPois.length === 0}
            <div class="empty-filtered">
              <p class="text-body-sm" style="color: var(--color-muted); margin: 0;">
                No places match the current filters.
              </p>
              <button class="btn btn-tertiary" type="button" onclick={clearAllFilters}>
                Show all
              </button>
            </div>
          {:else}
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-8 sm:gap-y-10">
              {#each filteredPois as poi (poi.id)}
                <div data-place-id={poi.placeId}>
                  <POICard
                    {poi}
                    groups={groups}
                    onOpen={(p) => (detailPoiId = p.id)}
                  />
                </div>
              {/each}
            </div>
          {/if}
        </section>
      {/if}
      {/if}
    </main>

    <footer class="py-6 text-caption-sm text-center" style="color: var(--color-muted); display: flex; flex-direction: column; align-items: center; gap: 0.5rem;">
      <svg viewBox="0 0 64 64" fill="currentColor" aria-hidden="true" style="width: 20px; height: 20px;">
        <path d="M 2 44 Q 12 42.5 22 44 T 42 44 T 62 44" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M 24 44 A 8 8 0 0 1 40 44 Z"/>
        <path d="M 4 51 Q 12 48 20 51 T 36 51 T 52 51 T 60 51" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-opacity="0.7"/>
        <path d="M 4 57 Q 12 54 20 57 T 36 57 T 52 57 T 60 57" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-opacity="0.45"/>
        <rect x="5" y="8" width="54" height="5"/>
        <rect x="14" y="13" width="5" height="9"/>
        <rect x="45" y="13" width="5" height="9"/>
        <rect x="8" y="22" width="48" height="5"/>
        <rect x="14" y="27" width="5" height="23"/>
        <rect x="45" y="27" width="5" height="23"/>
      </svg>
      <a href="/privacy" style="color: var(--color-muted);">Privacy</a>
    </footer>
  </div>

  {#if groupModalMode.kind !== 'closed'}
    <GroupModal
      tripToken={fetchState.trip.token}
      existingGroup={groupModalMode.kind === 'edit' ? groupModalMode.group : undefined}
      existingTints={groups.map((g) => g.tint)}
      onSaved={onGroupSaved}
      onClose={() => (groupModalMode = { kind: 'closed' })}
    />
  {/if}

  {#if manageGroupsOpen && fetchState.status === 'ready'}
    <ManageGroupsModal
      tripToken={fetchState.trip.token}
      groups={groups}
      poiCounts={groupCounts}
      onEdit={(grp) => (groupModalMode = { kind: 'edit', group: grp })}
      onDeleted={onGroupDeleted}
      onReorder={(next) => (groups = next)}
      onClose={() => (manageGroupsOpen = false)}
    />
  {/if}

  {#if manageCitiesOpen && fetchState.status === 'ready'}
    <ManageCitiesModal
      tripToken={fetchState.trip.token}
      cities={cities}
      onDelete={deleteCityNow}
      onReorder={(next) => (cities = next)}
      onClose={() => (manageCitiesOpen = false)}
    />
  {/if}

  {#if editGroupsForPoi !== null}
    <EditGroupsSheet
      tripToken={fetchState.trip.token}
      poi={editGroupsForPoi}
      groups={groups}
      onClose={() => (editGroupsForPoi = null)}
      onUpdated={onPoiUpdated}
      onCreateGroup={() => {
        const target = editGroupsForPoi;
        groupModalMode = {
          kind: 'create',
          afterCreate: target ? autoAssignNewGroupTo(target) : undefined,
        };
      }}
    />
  {/if}

  {#if detailPoi !== null && fetchState.status === 'ready'}
    <PoiDetailSheet
      poi={detailPoi}
      tripToken={fetchState.trip.token}
      editable={fetchState.trip.editable}
      groups={groups}
      cities={cities}
      activeCity={activeCity}
      routeEmpty={routeEmpty}
      inRoute={routePoiIds.has(detailPoi.id)}
      isCover={fetchState.trip.coverPoiId === detailPoi.id}
      hoursLocale={mapLanguage}
      onClose={() => (detailPoiId = null)}
      onUpdated={onPoiUpdated}
      onRemove={onRemovePoi}
      onNearby={onNearbyForPoi}
      onAddToRoute={(p) => addPoiToRoute(p.id)}
      onToggleCover={fetchState.trip.ownerId !== null
        ? () => onToggleCover(detailPoi.id)
        : undefined}
    />
  {/if}

  {#if sharing && fetchState.status === 'ready'}
    <ShareSheet
      token={fetchState.trip.token}
      onClose={() => (sharing = false)}
      permissions={fetchState.trip.ownerId !== null &&
      fetchState.trip.ownerId === fetchState.trip.viewerUserId
        ? {
            linkEditEnabled: fetchState.trip.linkEditEnabled,
            onChange: onLinkEditChange,
          }
        : undefined}
    />
  {/if}

  {#if nearby.kind !== 'closed' && fetchState.status === 'ready'}
    <NearbyResultsSheet
      title={nearbyLabels.title}
      subtitle={nearbyLabels.subtitle}
      results={nearbyResults}
      groups={groups}
      onPick={onPickFromNearby}
      onClose={() => (nearby = { kind: 'closed' })}
      onAddPlace={() => {
        nearby = { kind: 'closed' };
        // If nearby was opened from a POI's detail sheet, that sheet is
        // still mounted and would block clicks on the search input we're
        // about to focus. Close it before focusing.
        detailPoiId = null;
        searchInput?.focus();
      }}
    />
  {/if}

  {#if reveal !== null}
    <SurpriseReveal
      poi={reveal.poi}
      contextLabel={
        reveal.kind === 'surprise'
          ? `Surprise from ${reveal.sourceLabel}`
          : reveal.kind === 'closest'
            ? `Closest in ${reveal.sourceLabel}`
            : `Near ${reveal.sourceLabel}`
      }
      contextIcon={
        reveal.kind === 'surprise' ? 'shuffle' : reveal.kind === 'closest' ? 'map-pin' : 'sparkles'
      }
      distanceM={reveal.kind !== 'surprise' ? reveal.distanceM : undefined}
      originLabel={reveal.kind === 'closest' ? reveal.originLabel : undefined}
      locationBlocked={reveal.kind === 'closest' && reveal.locationBlocked}
      canPickAnother={reveal.kind === 'surprise' && reveal.members.length > 1}
      routeEmpty={routeEmpty}
      inRoute={routePoiIds.has(reveal.poi.id)}
      onClose={() => (reveal = null)}
      onAnother={pickAnother}
      onAddToRoute={(p) => addPoiToRoute(p.id)}
    />
  {/if}

  <LocationPrimer />

  {#if previewState.kind === 'open'}
    {@const wasDiscoveryFlow = previewState.discoveryFlow === true}
    <PoiPreviewSheet
      preview={previewState.preview}
      busy={previewState.busy}
      error={previewState.error}
      hoursLocale={mapLanguage}
      onConfirm={confirmBookmarkFromPreview}
      onClose={() => {
        previewState = { kind: 'closed' };
        if (wasDiscoveryFlow) {
          // Discovery: user dismissed without bookmarking. Deactivate
          // the focused pin (revert its coral highlight) — they're
          // back to browsing the map, not selecting this POI.
          focusedPlaceId = null;
        } else {
          // Autocomplete: refocus the input so the dropdown stays
          // visible with the same query + results. setTimeout(0)
          // defers past the Dialog's own focus restore.
          setTimeout(() => searchInput?.focus(), 0);
        }
      }}
    />
  {/if}

  {#if addCityState.kind === 'open'}
    <AddCityModal
      tripToken={fetchState.trip.token}
      initialQuery={addCityState.initialQuery ?? ''}
      onAdded={onCityAdded}
      onClose={() => (addCityState = { kind: 'closed' })}
    />
  {/if}

  {#if importUrlOpen && fetchState.status === 'ready' && fetchState.trip.editable}
    <ImportFromUrlModal
      tripToken={fetchState.trip.token}
      onResolved={({ placeId, displayName, sessionToken }) => {
        importUrlOpen = false;
        void onPickPlace(placeId, sessionToken, displayName);
      }}
      onClose={() => (importUrlOpen = false)}
    />
  {/if}

  {#if deleteTripState.kind !== 'closed'}
    <ConfirmDialog
      title="Delete this trip?"
      message="This cannot be undone. All places, groups, and cities in this trip will also be deleted."
      confirmLabel={deleteTripState.kind === 'deleting' ? 'Deleting…' : 'Delete trip'}
      destructive
      onConfirm={onConfirmDeleteTrip}
      onCancel={() => (deleteTripState = { kind: 'closed' })}
    />
  {/if}


  {#if chainedAction.kind === 'awaitingAuth'}
    {@const pendingAction = chainedAction.action}
    <AuthModal
      title={pendingAction === 'save' ? 'Save this trip' : 'Make a copy'}
      subline={pendingAction === 'save'
        ? "Sign in to keep this trip — we'll save it so you can find it again on any device."
        : 'Sign in to save your own copy of this trip — edit it freely without touching the original.'}
      onClose={() => (chainedAction = { kind: 'idle' })}
      onSuccess={async () => {
        await executeChainedAction(pendingAction);
      }}
    />
  {/if}

  {#if signInOpen}
    <AuthModal
      subline="Already have an account? Sign back in to see this trip alongside the rest of your trips on your dashboard."
      onClose={() => (signInOpen = false)}
    />
  {/if}

  {#if clearRouteConfirmOpen}
    <ConfirmDialog
      title="Clear all stops?"
      message="This will remove every stop from your route. Your bookmarked places aren't affected."
      confirmLabel="Clear"
      destructive
      onConfirm={clearRoute}
      onCancel={() => (clearRouteConfirmOpen = false)}
    />
  {/if}

{/if}

<style>
  /* Cities / group filter rows while discovery owns the map. Hidden
     entirely — the map shows discovery results, the filters don't
     apply to them, and showing them dimmed only crowds the screen.
     They come back automatically when discovery exits.
     !important wins over .filter-row's flex declaration below; an
     earlier attempt at plain `.filters-locked { display: none }` was
     overridden by the later .filter-row rule because both selectors
     have the same specificity. */
  .filters-locked { display: none !important; }

  /* Composite layout that lets the desktop discovery sidebar sit to
     the left of the map without affecting the map's own height. When
     the sidebar isn't rendered (mobile, or no discovery results) the
     stage is a single-child flex container — visually identical to
     dropping <MapView> in directly. */
  .map-stage {
    display: flex;
    gap: 1rem;
    align-items: stretch;
    min-width: 0;
    /* Pin the stage to the same height MapView uses for `.map-wrap`
       so the sidebar (via align-self: stretch) matches the map height.
       Without an explicit container height the flex container would
       grow to the tallest child — i.e. the sidebar's intrinsic
       content height when the result list is long. */
    height: clamp(420px, 70vh, 720px);
  }
  /* MapView's .map-wrap stretches inside the stage. Override the
     component's own clamp height so it fills the stage instead. */
  .map-stage :global(.map-wrap) {
    flex: 1;
    min-width: 0;
    height: auto;
  }
  @media (max-width: 767px) {
    /* No sidebar on mobile — keep the stage as a normal block so the
       map keeps its corners and the gap doesn't reserve space. */
    .map-stage { gap: 0; }
  }

  .filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    /* Negative outside margins + matching padding keep the row's
       focus rings within the viewport on mobile but let the row
       breathe past the page's content-column padding. */
    margin-left: -1.25rem;
    padding-left: 1.25rem;
    margin-right: -1.25rem;
    padding-right: 1.25rem;
  }
  /* The filter row hosts two sibling chip wrappers: membership
     (group/city) and value (rating/open-now). `display: contents`
     dissolves the wrappers so their chips become direct flex items
     of .filter-row — that way each chip wraps individually instead
     of the whole wrapper overflowing the viewport as a single
     inline-flex unit on narrow screens. The membership wrapper
     keeps the .filters-locked override to hide group chips during
     discovery mode. */
  .filter-group { display: contents; }
  .filter-values { display: contents; }
  /* Pull the filter row up so it sits as a sibling cluster with the
     cities row above it (parent <main> uses gap-8 between top-level
     siblings, which reads as wasted space between two filter rows). */
  .filter-row--tight { margin-top: -1.25rem; }
  @media (min-width: 640px) {
    .filter-row {
      margin: 0;
      padding: 0;
    }
    .filter-row--tight { margin-top: -1.25rem; }
  }
  .empty-filtered {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
  }
  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 1px solid var(--color-hairline);
    border-radius: 9999px;
    padding: 0.375rem 0.875rem;
    height: 32px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    flex: none;
    transition: background 100ms ease, border-color 100ms ease;
  }
  .filter-chip:hover { background: var(--color-surface-soft); }
  .filter-chip.active {
    background: var(--color-ink);
    color: var(--color-on-dark);
    border-color: var(--color-ink);
  }
  .filter-chip.ghost {
    background: transparent;
    color: var(--color-muted);
    border-style: dashed;
  }
  .filter-chip.ghost:hover { color: var(--color-ink); }
  /* Split chip: "+ New group" and the gear share one outer pill,
     separated by a 1px divider. Same dashed-border aesthetic as the
     ghost variant; each half is its own button with independent
     hover + tap. Saves horizontal space and wraps as one unit when
     the row breaks onto multiple lines. */
  .filter-chip.filter-chip--split {
    padding: 0;
    background: transparent;
    color: var(--color-muted);
    border-style: dashed;
    gap: 0;
    overflow: hidden;
  }
  .filter-chip--split .split-action {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: transparent;
    border: 0;
    padding: 0 0.75rem;
    height: 100%;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    color: inherit;
    cursor: pointer;
  }
  .filter-chip--split .split-action:hover { color: var(--color-ink); }
  /* Icon-only half (the gear) deserves a square-ish footprint — a
     bare 12px icon inside the default text padding feels cramped. */
  .filter-chip--split .split-action--icon {
    min-width: 36px;
    padding: 0 0.5rem;
    justify-content: center;
  }
  .filter-chip--split .split-divider {
    width: 1px;
    height: 60%;
    background: var(--color-hairline);
    flex: none;
  }
  /* Auto-group chip — visually distinct from user groups. Dashed border
     + soft background signals "computed slice", not "group I made". */
  .filter-chip.filter-chip--auto {
    background: var(--color-surface-soft);
    color: var(--color-ink);
    border-style: dashed;
    border-color: var(--color-border-strong);
  }
  .filter-chip.filter-chip--auto:hover {
    background: var(--color-surface-strong);
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-muted);
    text-decoration: none;
    padding: 0.25rem 0.5rem;
    margin-left: -0.5rem;
    border-radius: var(--radius-sm);
  }
  /* Brand-mark variant: ink color (more prominent than muted "back"
     text since it's the only path back to landing for signed-out
     viewers), slightly larger gap to balance the icon. */
  .back-link.brand-home {
    color: var(--color-ink);
    gap: 0.5rem;
  }
  .back-link.brand-home .back-label {
    font-size: 14px;
    font-weight: 600;
  }
  .back-link:hover {
    color: var(--color-ink);
    background: var(--color-surface-soft);
  }
  @media (max-width: 640px) {
    .back-label { display: none; }
  }

  /* `.trip-name` styling is applied via EditableTitle's `headingClass`
     prop. Svelte scopes class hashes per file, so the rules below only
     apply to elements rendered from this file. We use :global to push
     them through into the EditableTitle's heading element. */
  :global(.trip-name) {
    margin: 0;
    color: var(--color-ink);
    font-size: var(--text-display-lg);
    font-weight: var(--text-display-lg--font-weight);
    line-height: var(--text-display-lg--line-height);
    letter-spacing: var(--text-display-lg--letter-spacing);
  }
</style>
